import type { NuxtApp } from "#app";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { platformKey } from "~/constants/headerKeys";

/**
 * Sets request headers for client-side API requests.
 * Adds authorization token, platform ID, and analytics tracking context to the request headers.
 *
 * @param request - The Axios request object to modify
 * @param auth - Authentication composable for retrieving user tokens
 * @param platformStore - Platform store containing platform configuration
 * @param nuxtApp - Nuxt application instance for accessing Segment analytics
 * @returns The modified request object with updated headers
 */
async function setRequestHeadersForClientRequest(
  request: InternalAxiosRequestConfig,
  auth: ReturnType<typeof useAuth>,
  platformId: string | undefined,
  nuxtApp: NuxtApp,
) {
  try {
    request.headers.Authorization = await auth.getToken();
  } catch {
    // refreshing token failed
    await auth.logout();

    throw new Error("Unable to refresh token for client request.");
  }

  request.headers[platformKey] = platformId;
  const analyticsContext = getAnalyticsHeaders(nuxtApp);
  let anonymousId = "";
  if (nuxtApp.$segment.browser.value) {
    const user = await nuxtApp.$segment.browser.value.user();
    anonymousId = user?.anonymousId() ?? "";
  }
  analyticsContext.properties.anonymousId = anonymousId;

  request.headers["trackingContext"] = JSON.stringify(analyticsContext);

  return request;
}

export function setClientAxiosInterceptors(axios: AxiosInstance) {
  const auth = useAuth();
  const nuxtApp = useNuxtApp();
  const platformStore = usePlatformStore();

  axios.interceptors.request.use(async (req) => {
    const platformId = platformStore?.tryGetPlatformConfiguration?.platformId;

    return setRequestHeadersForClientRequest(req, auth, platformId, nuxtApp);
  });
}
